import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Heart, Search, Loader2, Trash2, Eye, Clock, CheckCircle2, XCircle, Video, Mail, Phone, User, FileText, Send } from 'lucide-react';

const API_URL = window.location.origin;

const STATUS_MAP = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
  reviewing: { label: 'Reviewing', color: 'bg-blue-100 text-blue-800' },
  interview: { label: 'Interview', color: 'bg-purple-100 text-purple-800' },
  selected: { label: 'Selected', color: 'bg-emerald-100 text-emerald-800' },
  waitlist: { label: 'Waitlist', color: 'bg-gray-100 text-gray-700' },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-800' },
};

const OPTION_LABELS = {
  gifted_experience: 'Gifted Experience',
  scholarship: 'Scholarship',
};

const AdminGiftApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [sendingSdcLink, setSendingSdcLink] = useState(false);

  const handleSendSdcLink = async (app) => {
    if (!app?.email || !app?.full_name) {
      toast.error('Application is missing name or email');
      return;
    }
    const sendEmail = window.confirm(
      `Send Sister Diamonds Collective payment link to ${app.full_name} (${app.email})?\n\nClick OK to also email them the link.\nClick Cancel to just generate the link without emailing.`
    );
    setSendingSdcLink(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/sdc-invitations`,
        {
          applicant_email: app.email,
          applicant_name: app.full_name,
          gift_application_id: app.id,
          send_email: sendEmail,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res?.data?.invitation_url;
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success(
            sendEmail
              ? `Email queued to ${app.email}. Link also copied to clipboard.`
              : `Link generated and copied to clipboard.`,
            { duration: 8000 }
          );
        } catch {
          toast.success(`Link generated: ${url}`, { duration: 12000 });
        }
        // Show admin the link in a prompt for easy copy/paste fallback
        window.prompt('SDC payment link (copy and share):', url);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create payment invitation');
    } finally {
      setSendingSdcLink(false);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/gift-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(res.data);
    } catch (err) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApplications(); }, []);

  const updateStatus = async (appId, newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/gift-applications/${appId}`, {
        status: newStatus,
        admin_notes: adminNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Application updated');
      fetchApplications();
      if (selectedApp?.id === appId) {
        setSelectedApp(prev => ({ ...prev, status: newStatus, admin_notes: adminNotes }));
      }
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const deleteApplication = async (appId) => {
    if (!window.confirm('Delete this application permanently?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/gift-applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application deleted');
      setViewOpen(false);
      fetchApplications();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openDetail = (app) => {
    setSelectedApp(app);
    setAdminNotes(app.admin_notes || '');
    setViewOpen(true);
  };

  const filtered = applications.filter(a => {
    const matchSearch = !searchTerm || 
      a.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewing: applications.filter(a => a.status === 'reviewing').length,
    selected: applications.filter(a => a.status === 'selected').length,
    gift: applications.filter(a => a.option_selected === 'gifted_experience').length,
    scholarship: applications.filter(a => a.option_selected === 'scholarship').length,
  };

  return (
    <div className="space-y-6" data-testid="admin-gift-applications">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Total', value: stats.total, color: 'bg-forest/10 text-forest' },
          { label: 'Pending', value: stats.pending, color: 'bg-amber-50 text-amber-700' },
          { label: 'Reviewing', value: stats.reviewing, color: 'bg-blue-50 text-blue-700' },
          { label: 'Selected', value: stats.selected, color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Gift Apps', value: stats.gift, color: 'bg-rose-50 text-rose-700' },
          { label: 'Scholarship', value: stats.scholarship, color: 'bg-purple-50 text-purple-700' },
        ].map((s, i) => (
          <Card key={i} className="card-base">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold" style={{ color: '#1A3C34' }}>{s.value}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="gift-app-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="gift-app-status-filter">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewing">Reviewing</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="selected">Selected</SelectItem>
                <SelectItem value="waitlist">Waitlist</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchApplications} disabled={loading} data-testid="gift-app-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <Heart className="h-5 w-5" style={{ color: '#C06C54' }} />
            Gift Retreat Applications ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-forest" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Heart className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p>No applications found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(app => {
                const statusInfo = STATUS_MAP[app.status] || STATUS_MAP.pending;
                return (
                  <div
                    key={app.id}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
                    onClick={() => openDetail(app)}
                    data-testid={`gift-app-row-${app.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C06C54' }}>
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-forest truncate">{app.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{app.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <Badge className={`text-xs ${OPTION_LABELS[app.option_selected] ? 'bg-forest/10 text-forest' : 'bg-gray-100'}`}>
                        {OPTION_LABELS[app.option_selected] || 'Unknown'}
                      </Badge>
                      <Badge className={`text-xs ${statusInfo.color}`}>{statusInfo.label}</Badge>
                      {app.video_url && <Video className="h-4 w-4 text-blue-500" />}
                      <span className="text-xs text-gray-400 hidden sm:block">
                        {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedApp && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-forest">
                  <Heart className="h-5 w-5" style={{ color: '#C06C54' }} />
                  {selectedApp.full_name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 mt-2">
                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-gray-50">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-forest">{selectedApp.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-forest">{selectedApp.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-forest">Age: {selectedApp.age_range || 'N/A'}</span>
                  </div>
                </div>

                {/* Option & Status */}
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-forest/10 text-forest">
                    {OPTION_LABELS[selectedApp.option_selected] || selectedApp.option_selected}
                  </Badge>
                  {selectedApp.option_selected === 'scholarship' && selectedApp.scholarship_percentage && (
                    <Badge className="bg-purple-100 text-purple-800">
                      Prepared to cover: {selectedApp.scholarship_percentage}
                    </Badge>
                  )}
                  <Badge className={STATUS_MAP[selectedApp.status]?.color || 'bg-gray-100'}>
                    {STATUS_MAP[selectedApp.status]?.label || selectedApp.status}
                  </Badge>
                  <span className="text-xs text-gray-400 self-center ml-auto">
                    Submitted: {selectedApp.submitted_at ? new Date(selectedApp.submitted_at).toLocaleString() : 'N/A'}
                  </span>
                </div>

                {/* Video */}
                {selectedApp.video_url && (
                  <div>
                    <p className="text-sm font-medium text-forest mb-2 flex items-center gap-2">
                      <Video className="h-4 w-4" style={{ color: '#C06C54' }} />
                      Video Submission
                    </p>
                    <video
                      controls
                      className="w-full rounded-xl bg-black max-h-[300px]"
                      src={selectedApp.video_url.startsWith('http') ? selectedApp.video_url : `${API_URL}${selectedApp.video_url}`}
                      data-testid="gift-app-video"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                {/* Written Responses */}
                <div className="space-y-4">
                  <p className="text-sm font-medium text-forest flex items-center gap-2">
                    <FileText className="h-4 w-4" style={{ color: '#C06C54' }} />
                    Written Responses
                  </p>
                  {[
                    { label: 'What are you currently carrying that most people may not see?', value: selectedApp.carrying_unseen },
                    { label: 'If you attended this retreat, what would you want to feel, experience, or approach differently when you return home?', value: selectedApp.return_differently },
                    { label: 'How would being supported in this season impact the people or responsibilities connected to you?', value: selectedApp.impact_on_others },
                  ].filter(q => q.value).map((q, i) => (
                    <div key={i} className="p-3 rounded-lg bg-cream/50 border border-forest/5">
                      <p className="text-xs font-medium text-forest/60 mb-1">{q.label}</p>
                      <p className="text-sm text-forest leading-relaxed">{q.value}</p>
                    </div>
                  ))}
                </div>

                {/* Logistics */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Cover Airfare', value: selectedApp.cover_airfare },
                    { label: 'Commit to Attending', value: selectedApp.commit_attending },
                    { label: 'Secure in 48hrs', value: selectedApp.secure_48hrs },
                  ].map((q, i) => (
                    <div key={i} className="p-3 rounded-lg bg-gray-50 text-center">
                      <p className="text-xs text-gray-500 mb-1">{q.label}</p>
                      <p className={`text-sm font-semibold ${q.value === 'Yes' ? 'text-emerald-600' : q.value === 'No' ? 'text-red-600' : 'text-amber-600'}`}>
                        {q.value || 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Confirmations */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Airfare not included', checked: selectedApp.confirm_airfare },
                    { label: 'VIP optional', checked: selectedApp.confirm_vip },
                    { label: 'Limited & intimate', checked: selectedApp.confirm_intimate },
                    { label: 'Selection-based', checked: selectedApp.confirm_selection },
                  ].map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {c.checked ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <XCircle className="h-3.5 w-3.5 text-gray-300" />}
                      <span className={c.checked ? 'text-forest' : 'text-gray-400'}>{c.label}</span>
                    </div>
                  ))}
                </div>

                {/* Final Reflection */}
                {selectedApp.final_reflection && (
                  <div className="p-3 rounded-lg bg-cream/50 border border-forest/5">
                    <p className="text-xs font-medium text-forest/60 mb-1">Final Reflection</p>
                    <p className="text-sm text-forest leading-relaxed italic">{selectedApp.final_reflection}</p>
                  </div>
                )}

                {/* Admin Controls */}
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-medium text-forest">Admin Actions</p>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 mb-1 block">Update Status</label>
                      <Select value={selectedApp.status} onValueChange={(val) => updateStatus(selectedApp.id, val)}>
                        <SelectTrigger data-testid="gift-app-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="reviewing">Reviewing</SelectItem>
                          <SelectItem value="interview">Interview</SelectItem>
                          <SelectItem value="selected">Selected</SelectItem>
                          <SelectItem value="waitlist">Waitlist</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteApplication(selectedApp.id)}
                      data-testid="gift-app-delete-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Admin Notes</label>
                    <Textarea
                      value={adminNotes}
                      onChange={e => setAdminNotes(e.target.value)}
                      placeholder="Internal notes about this application..."
                      className="min-h-[80px]"
                      data-testid="gift-app-notes"
                    />
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => updateStatus(selectedApp.id, selectedApp.status)}
                      disabled={updating}
                      data-testid="gift-app-save-notes"
                      style={{ backgroundColor: '#1A3C34' }}
                    >
                      {updating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      Save Notes
                    </Button>
                  </div>
                  {selectedApp.reviewed_by && (
                    <p className="text-xs text-gray-400">
                      Last reviewed by {selectedApp.reviewed_by} on {selectedApp.reviewed_at ? new Date(selectedApp.reviewed_at).toLocaleString() : 'N/A'}
                    </p>
                  )}

                  {/* SDC Payment Link — sends approved applicant a private payment page */}
                  <div
                    className="mt-4 p-4 rounded-lg"
                    style={{ backgroundColor: '#FFF7F2', border: '1px solid rgba(192,108,84,0.25)' }}
                  >
                    <p className="text-xs uppercase tracking-wide font-medium mb-2" style={{ color: '#C06C54' }}>
                      Sister Diamonds Collective
                    </p>
                    <p className="text-sm mb-3" style={{ color: '#1A3C34' }}>
                      Push a private payment page to <strong>{selectedApp.full_name}</strong> so they can choose their plan, accept the program agreements, and finalize their seat in SDC.
                    </p>
                    <Button
                      size="sm"
                      onClick={() => handleSendSdcLink(selectedApp)}
                      disabled={sendingSdcLink}
                      style={{ backgroundColor: '#C06C54', color: '#ffffff' }}
                      data-testid="send-sdc-payment-link-btn"
                    >
                      {sendingSdcLink ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send SDC Payment Link
                    </Button>
                    <p className="text-[11px] mt-2" style={{ color: 'rgba(26,60,52,0.55)' }}>
                      Generates a unique 30-day link, emails it to the applicant (if confirmed), and copies it to your clipboard for sharing manually.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGiftApplicationsTab;
